import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Camera, Monitor, AlertTriangle, Activity, MapPin } from 'lucide-react';

const SmartBuilding = () => {
  const systems = [
    {
      icon: Monitor,
      title: 'Integrated Command & Control Center (ICCC)',
      description: 'Centralized nerve center for real-time governance and public safety operations at the VVIP site.',
      features: [
        'Real-time monitoring dashboard',
        'Multi-source data integration',
        'Incident management and response',
        'Centralized control of all systems',
        'Analytics and reporting capabilities',
        'Emergency response coordination'
      ]
    },
    {
      icon: Activity,
      title: 'Environmental Sensors',
      description: 'Comprehensive sensor network monitoring environmental conditions for occupant safety and comfort.',
      features: [
        'Temperature and humidity monitoring',
        'Air quality sensors (CO2, PM2.5, VOC)',
        'Pressure differential monitoring',
        'Occupancy detection',
        'Energy consumption tracking'
      ]
    },
    {
      icon: AlertTriangle,
      title: 'CBRN Sensors',
      description: 'Advanced Chemical, Biological, and Radiological detection systems ensuring the highest security standards.',
      features: [
        'Chemical threat detection',
        'Biological agent monitoring',
        'Radiological sensors',
        'Real-time threat alerts',
        'Integration with emergency protocols',
        'Automated response activation'
      ]
    },
    {
      icon: Camera,
      title: 'Video Surveillance System',
      description: 'Comprehensive camera network providing complete visibility across the VVIP facility.',
      features: [
        'High-resolution IP cameras',
        '360-degree coverage',
        'Night vision capabilities',
        'Video analytics integration',
        'Facial recognition',
        'Behavior analysis',
        'Perimeter intrusion detection'
      ]
    }
  ];

  return (
    <div className="detail-page">
      <div className="detail-header-section">
        <div className="detail-header-overlay"></div>
        <img
          src="https://images.unsplash.com/photo-1599580546666-c26f15e00933?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3MTcyODk4NHww&ixlib=rb-4.1.0&q=85"
          alt="Smart Building"
          className="detail-header-image"
        />
        <div className="detail-header-content">
          <Link to="/" className="back-button-detail">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1 className="detail-title">Smart Building</h1>
          <p className="detail-subtitle">
            VVIP Site, New Delhi - Advanced ICCC for enhanced public safety and real-time governance
          </p>
        </div>
      </div>

      <div className="detail-container">
        <section className="detail-section">
          <h2 className="detail-section-title">Project Overview</h2>
          <div className="project-overview">
            <p className="project-description">
              The VVIP site in New Delhi represents a state-of-the-art implementation of Integrated Command 
              and Control Center technology, designed to ensure the highest levels of public safety and 
              operational excellence. The facility integrates advanced sensor networks, including Chemical, 
              Biological, and Radiological (CBRN) detection systems, with comprehensive video surveillance 
              to create an impenetrable security envelope while enabling real-time governance and rapid 
              incident response.
            </p>
            <div className="project-highlights">
              <div className="highlight-item">
                <MapPin className="highlight-icon" size={24} />
                <div>
                  <h4>Location</h4>
                  <p>VVIP Site, New Delhi</p>
                </div>
              </div>
              <div className="highlight-item">
                <Shield className="highlight-icon" size={24} />
                <div>
                  <h4>Security Level</h4>
                  <p>Highest VVIP Protection Standards</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Integrated Systems</h2>
          <div className="systems-grid">
            {systems.map((system, index) => (
              <div key={index} className="system-card">
                <div className="system-header">
                  <div className="system-icon-wrapper">
                    <system.icon size={28} />
                  </div>
                  <h3 className="system-title">{system.title}</h3>
                </div>
                <p className="system-description">{system.description}</p>
                <ul className="system-features">
                  {system.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">CBRN Detection Capabilities</h2>
          <div className="cbrn-grid">
            <div className="cbrn-card">
              <div className="cbrn-icon-container">
                <AlertTriangle size={32} />
              </div>
              <h4>Chemical Detection</h4>
              <p>Advanced sensors detect toxic industrial chemicals, nerve agents, and hazardous substances with immediate alert generation.</p>
              <ul className="cbrn-features">
                <li>Multi-gas detection arrays</li>
                <li>Parts-per-billion sensitivity</li>
                <li>Continuous air sampling</li>
                <li>Automated calibration</li>
              </ul>
            </div>
            <div className="cbrn-card">
              <div className="cbrn-icon-container">
                <Activity size={32} />
              </div>
              <h4>Biological Monitoring</h4>
              <p>Bioaerosol sensors identify biological threats including pathogens and toxins in real-time.</p>
              <ul className="cbrn-features">
                <li>Continuous air monitoring</li>
                <li>Particle counting and sizing</li>
                <li>Pathogen identification</li>
                <li>Alert threshold management</li>
              </ul>
            </div>
            <div className="cbrn-card">
              <div className="cbrn-icon-container">
                <Shield size={32} />
              </div>
              <h4>Radiological Sensors</h4>
              <p>Gamma and neutron detection systems provide early warning of radiological threats.</p>
              <ul className="cbrn-features">
                <li>Background radiation monitoring</li>
                <li>Isotope identification</li>
                <li>Dose rate measurement</li>
                <li>Spectrum analysis</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Public Safety Features</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h4>Real-Time Threat Detection</h4>
              <p>Continuous monitoring of chemical, biological, and radiological threats with instant alerting and automated response protocols.</p>
            </div>
            <div className="benefit-card">
              <h4>Comprehensive Surveillance</h4>
              <p>360-degree video coverage with AI-powered analytics for perimeter protection, facial recognition, and behavior analysis.</p>
            </div>
            <div className="benefit-card">
              <h4>Centralized Control</h4>
              <p>ICCC provides unified view of all systems, enabling coordinated response and real-time decision-making for governance.</p>
            </div>
            <div className="benefit-card">
              <h4>Environmental Safety</h4>
              <p>Air quality and environmental sensors ensure optimal conditions while detecting anomalies that could indicate threats.</p>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">System Integration</h2>
          <div className="architecture-content">
            <p>
              All systems are seamlessly integrated through the ICCC, creating a unified security and 
              governance platform:
            </p>
            <ul className="architecture-features">
              <li>Real-time data aggregation from all sensor networks</li>
              <li>Automated correlation of events across multiple systems</li>
              <li>Intelligent threat assessment and prioritization</li>
              <li>Coordinated emergency response activation</li>
              <li>Historical data analysis and pattern recognition</li>
              <li>Compliance reporting and audit trails</li>
              <li>Redundant systems for 24/7/365 uptime</li>
            </ul>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Key Outcomes</h2>
          <div className="outcomes-list">
            <div className="outcome-item">
              <div className="outcome-number">01</div>
              <div className="outcome-content">
                <h4>Enhanced Security</h4>
                <p>Multi-layered CBRN detection and video surveillance create comprehensive protection</p>
              </div>
            </div>
            <div className="outcome-item">
              <div className="outcome-number">02</div>
              <div className="outcome-content">
                <h4>Real-Time Governance</h4>
                <p>ICCC enables instant situational awareness and data-driven decision making</p>
              </div>
            </div>
            <div className="outcome-item">
              <div className="outcome-number">03</div>
              <div className="outcome-content">
                <h4>Rapid Response</h4>
                <p>Automated alerting and coordinated protocols ensure swift incident management</p>
              </div>
            </div>
            <div className="outcome-item">
              <div className="outcome-number">04</div>
              <div className="outcome-content">
                <h4>Operational Excellence</h4>
                <p>Integrated systems reduce complexity while improving efficiency and reliability</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SmartBuilding;
